import { useState } from 'react';
import {
  CheckCircle,
  XCircle,
  Clock,
  Wallet,
  RefreshCw,
  AlertTriangle,
  User,
} from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { AgentHeader } from '../../components/layout/AgentHeader';
import { AgentSidebar } from '../../components/layout/AgentSidebar';
import { BottomNav } from '../../components/layout/BottomNav';
import { Card, CardContent } from '../../components/common/Card';
import { Button } from '../../components/common/Button';
import { Modal } from '../../components/common/Modal';
import { useFloatRechargeAdmin } from '../../hooks/useFloatRecharge';
import type {
  AdminRechargeRequest,
  RechargeMethod,
  RechargeStatus,
} from '../../types/float-recharge.types';

// ─── Constants ────────────────────────────────────────────────────────────────

const METHOD_LABELS: Record<RechargeMethod, string> = {
  INTERNAL_TRANSFER: 'Transferencia Interna',
  BANK_TRANSFER: 'Transferencia Bancaria',
  CASH_DEPOSIT: 'Depósito en Efectivo',
  MOBILE_MONEY: 'Mobile Money',
  CASH_PICKUP: 'Recogida en Efectivo',
};

const STATUS_CONFIG: Record<
  RechargeStatus,
  { label: string; color: string; bg: string; Icon: React.ElementType }
> = {
  PENDING: {
    label: 'Pendiente',
    color: 'text-yellow-700',
    bg: 'bg-yellow-100',
    Icon: Clock,
  },
  APPROVED: {
    label: 'Aprobada',
    color: 'text-green-700',
    bg: 'bg-green-100',
    Icon: CheckCircle,
  },
  REJECTED: {
    label: 'Rechazada',
    color: 'text-red-700',
    bg: 'bg-red-100',
    Icon: XCircle,
  },
};

const FLOAT_STATUS_COLORS: Record<string, string> = {
  OK: 'text-green-600',
  LOW: 'text-yellow-600',
  CRITICAL: 'text-orange-600',
  DEPLETED: 'text-red-600',
};

// ─── Reject Modal ─────────────────────────────────────────────────────────────

function RejectModal({
  open,
  agentName,
  amount,
  onClose,
  onReject,
  isPending,
}: {
  open: boolean;
  agentName: string;
  amount: number;
  onClose: () => void;
  onReject: (reason: string) => void;
  isPending: boolean;
}) {
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = () => {
    if (!reason.trim()) {
      setError('El motivo de rechazo es obligatorio');
      return;
    }
    onReject(reason.trim());
  };

  const handleClose = () => {
    setReason('');
    setError('');
    onClose();
  };

  return (
    <Modal
      isOpen={open}
      onClose={handleClose}
      title="Rechazar Solicitud"
    >
      <div className="space-y-4">
        <div className="bg-red-50 border border-red-100 rounded-lg p-3">
          <p className="text-sm text-red-800">
            Vas a rechazar la solicitud de{' '}
            <span className="font-semibold">{agentName}</span> por{' '}
            <span className="font-semibold">{amount.toLocaleString()} XAF</span>.
          </p>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Motivo del rechazo <span className="text-red-500">*</span>
          </label>
          <textarea
            rows={3}
            placeholder="Ej: Documentación insuficiente, monto excede el límite..."
            value={reason}
            onChange={(e) => {
              setReason(e.target.value);
              setError('');
            }}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 resize-none focus:outline-none focus:ring-2 focus:ring-red-400"
          />
          {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
        </div>
        <div className="flex gap-3">
          <Button variant="outline" className="flex-1" onClick={handleClose}>
            Cancelar
          </Button>
          <Button
            className="flex-1 bg-red-600 hover:bg-red-700 border-red-600 hover:border-red-700"
            onClick={handleSubmit}
            disabled={isPending}
          >
            {isPending ? (
              <span className="flex items-center gap-2">
                <RefreshCw size={14} className="animate-spin" /> Procesando...
              </span>
            ) : (
              'Confirmar Rechazo'
            )}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

// ─── Request Row ──────────────────────────────────────────────────────────────

function RequestRow({
  req,
  onApprove,
  onReject,
  approving,
}: {
  req: AdminRechargeRequest;
  onApprove: (id: string) => void;
  onReject: (req: AdminRechargeRequest) => void;
  approving: boolean;
}) {
  const cfg = STATUS_CONFIG[req.status];
  const StatusIcon = cfg.Icon;
  const floatColor = FLOAT_STATUS_COLORS[req.agent.floatStatus] ?? 'text-gray-600';
  const isPending = req.status === 'PENDING';

  return (
    <div className="p-4 border border-gray-200 rounded-xl bg-white hover:shadow-sm transition-shadow">
      <div className="flex flex-col sm:flex-row sm:items-start gap-4">
        {/* Agent info */}
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center shrink-0">
            <User size={18} className="text-purple-600" />
          </div>
          <div className="min-w-0">
            <p className="font-semibold text-gray-900 truncate">
              {req.agent.businessName}
            </p>
            <p className="text-xs text-gray-500">
              {req.agent.agentCode} · {req.agent.user.phone}
            </p>
            <p className={`text-xs font-medium mt-0.5 ${floatColor}`}>
              Float actual: {req.agent.currentFloat.toLocaleString()} XAF
              <span className="text-gray-400 font-normal">
                {' '}({req.agent.floatStatus})
              </span>
            </p>
          </div>
        </div>

        {/* Amount + status */}
        <div className="flex flex-col items-end shrink-0 gap-1">
          <span className="text-lg font-bold text-gray-900">
            {req.amount.toLocaleString()} XAF
          </span>
          <span
            className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${cfg.bg} ${cfg.color}`}
          >
            <StatusIcon size={11} />
            {cfg.label}
          </span>
        </div>
      </div>

      {/* Meta */}
      <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500">
        <span>{METHOD_LABELS[req.method]}</span>
        <span>
          {format(new Date(req.requestedAt), "d MMM yyyy, HH:mm", { locale: es })}
        </span>
        {req.notes && (
          <span className="text-gray-600 italic truncate max-w-xs">
            "{req.notes.split('\n')[0]}"
          </span>
        )}
      </div>

      {/* Approve / Reject */}
      {isPending && (
        <div className="mt-4 flex gap-2">
          <Button
            className="flex-1 sm:flex-none"
            onClick={() => onApprove(req.id)}
            disabled={approving}
          >
            {approving ? (
              <RefreshCw size={14} className="animate-spin" />
            ) : (
              <CheckCircle size={14} className="mr-1.5" />
            )}
            Aprobar
          </Button>
          <Button
            variant="outline"
            className="flex-1 sm:flex-none text-red-600 border-red-300 hover:bg-red-50"
            onClick={() => onReject(req)}
            disabled={approving}
          >
            <XCircle size={14} className="mr-1.5" />
            Rechazar
          </Button>
        </div>
      )}

      {/* Processed info */}
      {!isPending && req.approver && (
        <p className="mt-3 text-xs text-gray-400">
          {req.status === 'APPROVED' ? '✓ Aprobado' : '✗ Rechazado'} por{' '}
          {req.approver.firstName} {req.approver.lastName}
          {req.approvedAt && (
            <> · {format(new Date(req.approvedAt), "d MMM HH:mm", { locale: es })}</>
          )}
        </p>
      )}
    </div>
  );
}

// ─── Main Admin Page ──────────────────────────────────────────────────────────

type StatusFilter = 'ALL' | RechargeStatus;

const FILTER_TABS: { label: string; value: StatusFilter }[] = [
  { label: 'Todas', value: 'ALL' },
  { label: 'Pendientes', value: 'PENDING' },
  { label: 'Aprobadas', value: 'APPROVED' },
  { label: 'Rechazadas', value: 'REJECTED' },
];

export default function FloatRechargeAdminPage() {
  const [filter, setFilter] = useState<StatusFilter>('PENDING');
  const [rejectTarget, setRejectTarget] = useState<AdminRechargeRequest | null>(
    null
  );
  const [approvingId, setApprovingId] = useState<string | null>(null);

  const { requests, meta, isLoading, isError, approveMutation, rejectMutation } =
    useFloatRechargeAdmin({
      status: filter === 'ALL' ? undefined : filter,
      limit: 50,
    });

  const pendingCount = filter === 'ALL'
    ? requests.filter((r) => r.status === 'PENDING').length
    : filter === 'PENDING' ? requests.length : 0;

  const handleApprove = async (id: string) => {
    setApprovingId(id);
    try {
      await approveMutation.mutateAsync(id);
    } finally {
      setApprovingId(null);
    }
  };

  const handleReject = async (reason: string) => {
    if (!rejectTarget) return;
    try {
      await rejectMutation.mutateAsync({
        requestId: rejectTarget.id,
        reason,
      });
      setRejectTarget(null);
    } catch {
      // handled in hook
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <AgentHeader />

      <div className="flex">
        <AgentSidebar />

        <main className="flex-1 p-4 lg:p-6 pb-20 lg:pb-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">
                Solicitudes de Float
              </h2>
              <p className="text-sm text-gray-500">
                Panel de administración · {meta?.total ?? 0} solicitudes
              </p>
            </div>
            {pendingCount > 0 && (
              <div className="flex items-center gap-2 bg-yellow-100 text-yellow-800 px-3 py-1.5 rounded-full text-sm font-medium">
                <AlertTriangle size={14} />
                {pendingCount} pendiente{pendingCount !== 1 ? 's' : ''}
              </div>
            )}
          </div>

          {/* Filter tabs */}
          <div className="flex gap-2 mb-5 overflow-x-auto pb-1">
            {FILTER_TABS.map((tab) => (
              <button
                key={tab.value}
                onClick={() => setFilter(tab.value)}
                className={`shrink-0 px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
                  filter === tab.value
                    ? 'bg-purple-600 text-white'
                    : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* List */}
          {isLoading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-28 bg-gray-200 rounded-xl animate-pulse" />
              ))}
            </div>
          ) : isError ? (
            <Card variant="bordered">
              <CardContent className="py-12 text-center">
                <p className="text-gray-500">Error al cargar las solicitudes</p>
              </CardContent>
            </Card>
          ) : requests.length === 0 ? (
            <Card variant="bordered">
              <CardContent className="py-16 text-center">
                <Wallet size={40} className="mx-auto text-gray-300 mb-3" />
                <p className="font-medium text-gray-700">
                  {filter === 'PENDING'
                    ? 'No hay solicitudes pendientes'
                    : 'No hay solicitudes en este estado'}
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {requests.map((req) => (
                <RequestRow
                  key={req.id}
                  req={req}
                  onApprove={handleApprove}
                  onReject={setRejectTarget}
                  approving={approvingId === req.id}
                />
              ))}
            </div>
          )}
        </main>
      </div>

      <BottomNav />

      {rejectTarget && (
        <RejectModal
          open={true}
          agentName={rejectTarget.agent.businessName}
          amount={rejectTarget.amount}
          onClose={() => setRejectTarget(null)}
          onReject={handleReject}
          isPending={rejectMutation.isPending}
        />
      )}
    </div>
  );
}
